import { useMemo } from "react";
import { useSortBy, useTable } from "react-table";
import { DefaultCellForColumn } from "../../Table/DefaultCellForColumn";
import { DefaultHeaderForColumn } from "../../Table/DefaultHeaderForColumn";
import { ITableColumn } from "../../Table/TableColumn";
import {
  Align,
  TableHeaderCell,
  TableLoader,
  TableWrapper,
} from "../../Table/style";
import { IPlan } from "../../../hooks/api/admin/useAccountLiquidationPlans";
import { IMarket } from "../../../hooks/api/aevo/useMarkets";

interface IPlanTableProps {
  plans: IPlan[];
  marketsData?: IMarket[];
  isLoading?: boolean;
  isMobile?: boolean;
}

export function PlanTable({ plans, isLoading, marketsData }: IPlanTableProps) {
  const columns: ITableColumn<IPlan>[] = useMemo(
    () => [
      {
        title: "Instrument Name",
        align: "left",
        accessor: "instrument",
        Cell: ({ row }) => {
          const instrument = marketsData?.find(
            (market) => Number(market.instrument_id) === row.original.instrument
          );
          return (
            <Align align="left">
              {instrument ? instrument.instrument_name : "OPTIONS"}
            </Align>
          );
        },
      },
      {
        title: "Side",
        align: "right",
        accessor: "is_buy",
        Cell: ({ row }) => (
          <Align align="right">{row.original.is_buy ? "Buy" : "Sell"}</Align>
        ),
      },
      {
        title: "Amount",
        align: "right",
        accessor: "amount",
        Cell: ({ row }) => (
          <Align align="right">{Number(row.original.amount)}</Align>
        ),
      },
      {
        title: "Price",
        align: "right",
        accessor: "limit_price",
        Cell: ({ row }) => (
          <Align align="right">{Number(row.original.limit_price)}</Align>
        ),
      },
    ],
    []
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: plans,
        defaultColumn: {
          Header: DefaultHeaderForColumn,
          Cell: DefaultCellForColumn,
        } as any,
        autoResetSortBy: false,
      },
      useSortBy
    );

  return (
    <TableWrapper>
      <TableLoader isLoading={isLoading} />
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            // eslint-disable-next-line react/jsx-key
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: any) => (
                // eslint-disable-next-line react/jsx-key
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  style={{ width: column.width }}
                >
                  {column.render("Header")}
                </TableHeaderCell>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row);
            const { ...rowProps } = row.getRowProps();
            return (
              // eslint-disable-next-line react/jsx-key
              <tr {...rowProps}>
                {row.cells.map((cell) => (
                  // eslint-disable-next-line react/jsx-key
                  <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </TableWrapper>
  );
}
